import { take, call, put, select } from 'redux-saga/effects'
import { apiSaga } from './sagas'
import { api, dailyLinkCache } from '../../api/api'
import {
  normalizeErrorResponse,
  normalizeDailyLinkError,
} from '../../util/errorUtils'
import format from 'date-fns/format'
import { LOAD_CHECKOUT_CART_SUCCESS } from './cart'
import { applyCoupon } from './checkout'
import {
  getDailyLinkCoupon,
  getDailyLinkOwner,
  selectCurrentUser,
} from './selectors'

// ------------------------------------
// Action Types & Creators
// ------------------------------------
export const CREATE_DAILY_LINK = 'foodsby/dailyLink/CREATE_DAILY_LINK'
export const CREATE_DAILY_LINK_SUCCESS =
  'foodsby/dailyLink/CREATE_DAILY_LINK_SUCCESS'
export const CREATE_DAILY_LINK_FAILURE =
  'foodsby/dailyLink/CREATE_DAILY_LINK_FAILURE'

export const GET_DAILY_LINK_FOR_DATE =
  'foodsby/dailyLink/GET_DAILY_LINK_FOR_DATE'
export const GET_DAILY_LINK_FOR_DATE_SUCCESS =
  'foodsby/dailyLink/GET_DAILY_LINK_FOR_DATE_SUCCESS'
export const GET_DAILY_LINK_FOR_DATE_FAILURE =
  'foodsby/dailyLink/GET_DAILY_LINK_FOR_DATE_FAILURE'

export const CONSUME_DAILY_LINK = 'foodsby/dailyLink/CONSUME_DAILY_LINK'
export const CONSUME_DAILY_LINK_SUCCESS =
  'foodsby/dailyLink/CONSUME_DAILY_LINK_SUCCESS'
export const CONSUME_DAILY_LINK_FAILURE =
  'foodsby/dailyLink/CONSUME_DAILY_LINK_FAILURE'

export const CLEAR_DAILY_LINK = 'foodsby/dailyLink/CLEAR_DAILY_LINK'
export const CLEAR_DAILY_LINK_ERRORS =
  'foodsby/dailyLink/CLEAR_DAILY_LINK_ERROR'

export const createDailyLinkStart = () => ({
  type: CREATE_DAILY_LINK,
})

export const createDailyLinkSuccess = response => ({
  type: CREATE_DAILY_LINK_SUCCESS,
  payload: { response },
})

export const createDailyLinkFailure = error => ({
  type: CREATE_DAILY_LINK_FAILURE,
  error,
})

export const getDailyLinkForDateStart = (date, options = {}) => ({
  type: GET_DAILY_LINK_FOR_DATE,
  payload: { date, options },
})

export const getDailyLinkForDateSuccess = response => ({
  type: GET_DAILY_LINK_FOR_DATE_SUCCESS,
  payload: { response },
})

export const getDailyLinkForDateFailure = error => ({
  type: GET_DAILY_LINK_FOR_DATE_FAILURE,
  error,
})

export const consumeDailyLinkStart = (token, options = {}) => ({
  type: CONSUME_DAILY_LINK,
  payload: { token, options },
})

export const consumeDailyLinkSuccess = response => ({
  type: CONSUME_DAILY_LINK_SUCCESS,
  payload: { response },
})

export const consumeDailyLinkFailure = error => ({
  type: CONSUME_DAILY_LINK_FAILURE,
  error,
})

export const clearDailyLink = () => ({
  type: CLEAR_DAILY_LINK,
})

export const clearDailyLinkErrors = () => ({
  type: CLEAR_DAILY_LINK_ERRORS,
})

// ------------------------------------
// Action Handlers
// ------------------------------------

const ACTION_HANDLERS = {
  [CREATE_DAILY_LINK]: state => ({
    ...state,
    createDailyLinkLoading: true,
  }),
  [CREATE_DAILY_LINK_SUCCESS]: (state, action) => ({
    ...state,
    createDailyLinkLoading: false,
  }),
  [CREATE_DAILY_LINK_FAILURE]: (state, action) => ({
    ...state,
    createDailyLinkLoading: false,
    createDailyLinkError: normalizeErrorResponse(action.error),
  }),
  [GET_DAILY_LINK_FOR_DATE]: (state, action) => ({
    ...state,
    getDailyLinkLoading: true,
  }),
  [GET_DAILY_LINK_FOR_DATE_SUCCESS]: (state, action) => ({
    ...state,
    getDailyLinkLoading: false,
    usersDailyLink: action.payload.response.dailyLinkResult || null,
  }),
  [GET_DAILY_LINK_FOR_DATE_FAILURE]: (state, action) => ({
    ...state,
    getDailyLinkLoading: false,
    getDailyLinkForDateError: normalizeErrorResponse(action.error),
    usersDailyLink: undefined,
    dailyLinkCoupon: undefined,
  }),
  [CONSUME_DAILY_LINK]: (state, action) => ({
    ...state,
    consumeDailyLinkLoading: true,
  }),
  [CONSUME_DAILY_LINK_SUCCESS]: (state, action) => ({
    ...state,
    consumeDailyLinkLoading: false,
    dailyLinkCoupon: action.payload.response.data,
    dailyLinkOwner: action.payload.response.owner,
  }),
  [CONSUME_DAILY_LINK_FAILURE]: (state, action) => ({
    ...state,
    consumeDailyLinkLoading: false,
    consumeDailyLinkError: normalizeDailyLinkError(action.error),
    dailyLinkCoupon: undefined,
  }),
  [CLEAR_DAILY_LINK]: (state, action) => ({
    initialState,
  }),
  [CLEAR_DAILY_LINK_ERRORS]: (state, action) => ({
    ...state,
    createDailyLinkError: undefined,
    getDailyLinkForDateError: undefined,
    consumeDailyLinkError: undefined,
  }),
}

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {}

export default function dailyLink(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

// ------------------------------------
// Sagas
// ------------------------------------

export function* watchCreateDailyLink() {
  while (true) {
    yield take(CREATE_DAILY_LINK)
    yield call(
      apiSaga,
      createDailyLink,
      [format(new Date(), 'YYYY-MM-DD')],
      createDailyLinkSuccess,
      createDailyLinkFailure,
    )
  }
}

export function* watchCreateDailyLinkSuccess() {
  while (true) {
    yield take(CREATE_DAILY_LINK_SUCCESS)
    yield put(
      getDailyLinkForDateStart(format(new Date(), 'YYYY-MM-DD'), {
        forceUpdate: true,
      }),
    )
  }
}

export function* watchGetDailyLinkForDate() {
  while (true) {
    const {
      payload: { date, options },
    } = yield take(GET_DAILY_LINK_FOR_DATE)
    yield call(
      apiSaga,
      getDailyLinkForDate,
      [date, options],
      getDailyLinkForDateSuccess,
      getDailyLinkForDateFailure,
    )
  }
}

export function* watchGetDailyLinkForDateSuccess() {
  while (true) {
    const {
      payload: {
        response: { dailyLinkResult },
      },
    } = yield take(GET_DAILY_LINK_FOR_DATE_SUCCESS)
    if (dailyLinkResult === null) {
      yield put(createDailyLinkStart())
    }
  }
}

export function* watchConsumeDailyLink() {
  while (true) {
    const {
      payload: { token, options },
    } = yield take(CONSUME_DAILY_LINK)
    if (token) {
      yield call(
        apiSaga,
        getDailyLink,
        [token, options],
        consumeDailyLinkSuccess,
        consumeDailyLinkFailure,
      )
    } else {
      yield put(consumeDailyLinkFailure('Not a valid daily link token.'))
    }
  }
}

export function* watchConsumeDailyLinkSuccess() {
  while (true) {
    const {
      payload: {
        response: { owner },
      },
    } = yield take(CONSUME_DAILY_LINK_SUCCESS)
    const currentUser = yield select(selectCurrentUser)
    if (currentUser && owner === currentUser.userId) {
      yield put(
        consumeDailyLinkFailure(
          '😬 This is your link. You won’t receive a discount from ordering from your own link.',
        ),
      )
    }
  }
}

export function* watchAutoApplyDailyLink() {
  while (true) {
    const {
      payload: { checkoutCart },
    } = yield take(LOAD_CHECKOUT_CART_SUCCESS)
    if (
      checkoutCart &&
      checkoutCart.invoice &&
      checkoutCart.invoice.couponUsage &&
      !checkoutCart.invoice.couponUsage.couponApplied
    ) {
      const coupon = yield select(getDailyLinkCoupon)
      const dailyLinkOwner = yield select(getDailyLinkOwner)
      const currentUser = yield select(selectCurrentUser)
      if (coupon && dailyLinkOwner !== currentUser.userId) {
        yield put(applyCoupon(coupon))
        yield put(clearDailyLink())
      }
    }
  }
}

export const createDailyLink = onDate =>
  api.post(`/api/v1/shareables?type=DISCOUNT&onDate=${onDate}`)

export const getDailyLinkForDate = (date, options = {}) =>
  api.get(`/api/v1/shareables/date?onDate=${date}`, null, {
    cache: dailyLinkCache,
    ...options,
  })

export const getDailyLink = (token, options = {}) =>
  api.get(`/api/v1/shareables?token=${token}&onlyValid=true`, null, {
    cache: false,
    ...options,
  })
